import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Nfc } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

// Where a site tag lands when the phone has no Fire360Pro app: the tag's URL
// (https://fireacms.com/t/{code}) opens here — from an NFC tap, or from the label's QR code
// (?via=qr, so the visit log shows it was a camera scan) — we resolve it and jump to that site's
// Live Monitor.
// Not logged in → login first, then back here (Login honours location.state.from).
// Spec: docs/mobile/NFC_INTEGRATION.md §2.0.
export default function NfcTagOpen() {
  const { code } = useParams();
  const { user, loading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const source = params.get("via") === "qr" ? "qr" : "web";
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get(`/nfc/resolve/${encodeURIComponent(code)}`, { params: { source } })
      .then((r) => {
        const d = r.data;
        const q = new URLSearchParams();
        if (d.org_id) q.set("orgId", d.org_id);
        q.set("siteId", d.site_id);
        nav(`/live-monitor?${q.toString()}`, { replace: true });
      })
      .catch((e) => setError(e.response?.status === 404
        ? "This tag isn't linked to a site you have access to."
        : (e.response?.data?.detail || "Couldn't open this tag. Check your connection and try again.")));
  }, [user, code, nav, source]);

  if (loading) return <Shell><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></Shell>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;

  return (
    <Shell>
      {error ? (
        <div className="text-center space-y-3">
          <p className="text-sm text-gray-700">{error}</p>
          <Link to="/dashboard" className="text-sm font-semibold text-orange-600 hover:underline">Go to dashboard →</Link>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="w-4 h-4 animate-spin text-orange-500" /> Opening site…
        </div>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 w-full max-w-sm flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center">
          <Nfc className="w-7 h-7" strokeWidth={1.75} />
        </div>
        <img src="/logo.jpg" alt="Fire360Pro" className="h-8" />
        {children}
      </div>
    </div>
  );
}
